import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import { ExtensionMethod } from '../method'
import { argsFromParams, opaqueParam } from '../model/method-model'
import type { SharedExtensionMethod, SharedExtensionParam } from '../model/model-types'
import { toBodyParams } from '../model/params-model'
import { MethodParams } from '../params'
import {
  dynamicParamsLoaderName,
  type DynamicParamsOwner,
  type LoaderOwner,
  sampleResultLoaderName,
} from './loader-owner'
import { LoaderPayloadSchema } from './payload-schema'

// The methods the RUNTIME generates from what an author declared — never written by hand:
//
//   result: fn      → a SAMPLE_RESULT_LOADER   the console calls to preview the output shape
//   dynamicParams   → a PARAM_SCHEMA_DEFINITION that resolves extra top-level param fields
//
// Both are invoked with `{ payload: { criteria } }` — the values of the params they depend on. They obey the
// same policy as every other declared slot, so a sample-result loader (whose criteria ARE its owner's params)
// only resolves once the owner's required params are filled.

abstract class LoaderMethod extends ExtensionMethod {
  constructor(extension: Extension, protected readonly owner: LoaderOwner) {
    super(extension)
  }

  // The params this loader's criteria are checked against.
  protected abstract get criteria(): MethodParams

  validate(methodData: Record<string, unknown>): Record<string, unknown> {
    const input = this.input(methodData)
    const payload = input.parseField<{ criteria?: Record<string, unknown> }>('payload', LoaderPayloadSchema)

    // Undeclared keys ride through: the node's other params come along, and the owner's `result` fn may read
    // fields its `dynamicParams` generated.
    return this.criteria.normalize(payload.criteria, input.invalidCriteria(), { carryUnknown: true })
  }

  // Internal (SYSTEM-like): no icon, no colours, no sample. Its params are left opaque in the model — it is
  // invoked with `{ criteria }` at runtime, not through a rendered form.
  toModel(): SharedExtensionMethod {
    const bodyParams: SharedExtensionParam[] = [opaqueParam('payload')]

    return {
      name: this.name,
      operationName: this.name,
      description: '',
      registerAs: this.registerAs,
      category: this.owner.category,
      colorStart: '',
      colorEnd: '',
      customIcon: undefined,
      returnType: this.returnType,
      queryParams: [],
      bodyParams,
      metaInfo: { args: argsFromParams(bodyParams), sampleResult: null, sampleResultLoader: null },
    }
  }

  protected abstract get returnType(): string
}

// Derives the sample output from the params filled in so far — for shapes that aren't known statically
// (user-defined fields, a record whose columns depend on which table was picked).
export class SampleResultLoaderMethod extends LoaderMethod {
  get name(): string {
    return sampleResultLoaderName(this.owner.id)
  }

  get registerAs(): string {
    return 'SAMPLE_RESULT_LOADER'
  }

  protected get returnType(): string {
    return 'OBJECT'
  }

  // The sample varies with ANY param of the owner, so all of them are its criteria.
  protected get criteria(): MethodParams {
    return this.owner.params
  }

  execute(criteria: Record<string, unknown>, ctx: ExecutionContext): unknown {
    return this.owner.result.runLoader(ctx.bagWith({ params: criteria }))
  }
}

// Resolves additional top-level param fields at runtime, returned as the v1 param model the console renders.
export class DynamicParamsLoaderMethod extends LoaderMethod {
  constructor(extension: Extension, protected readonly owner: DynamicParamsOwner) {
    super(extension, owner)
  }

  get name(): string {
    return dynamicParamsLoaderName(this.owner.id)
  }

  get registerAs(): string {
    return 'PARAM_SCHEMA_DEFINITION'
  }

  protected get returnType(): string {
    return 'LIST'
  }

  protected get criteria(): MethodParams {
    return this.owner.dynamicParamsCriteria
  }

  async execute(criteria: Record<string, unknown>, ctx: ExecutionContext): Promise<SharedExtensionParam[]> {
    const schema = await this.owner.resolveDynamicParams(ctx.bagWith({ criteria }))

    return toBodyParams(MethodParams.of(schema, {
      serviceId: this.extension.id,
      kind: 'action',
      itemId: this.owner.id,
      slot: 'dynamicParams.resolve()',
    }))
  }
}
